// src/pages/finance-portal/fixed-assets/Assets.js
//
// Asset Register

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Card, Chip, IconButton, MenuItem, Paper, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK, ASSET_STATUS_COLORS,
    formatCurrency, formatDate, statusChip, humanizeStatus,
} from './components/fixedAssetFormatters';

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'FULLY_DEPRECIATED', label: 'Fully Depreciated' },
    { value: 'IMPAIRED', label: 'Impaired' },
    { value: 'HELD_FOR_SALE', label: 'Held For Sale' },
    { value: 'DISPOSED', label: 'Disposed' },
];

const headSx = {
    fontFamily: FA_FONT, fontWeight: 700, fontSize: '0.74rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};
const cellSx = {
    fontFamily: FA_FONT, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

export default function Assets() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categories, setCategories] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState('');

    const fetchAssets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: perPage };
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            if (categoryFilter) params.category_id = categoryFilter;
            const res = await axiosInstance.get('/api/finance/fixed-assets/assets', { params });
            if (res.data?.success) {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load assets');
        } finally {
            setLoading(false);
        }
    }, [page, perPage, search, statusFilter, categoryFilter]);

    useEffect(() => { fetchAssets(); }, [fetchAssets]);

    useEffect(() => {
        axiosInstance.get('/api/finance/fixed-assets/categories')
            .then(r => { if (r.data?.success) setCategories(r.data.data || []); })
            .catch(() => { /* silent */ });
    }, []);

    const handleActivate = async (id) => {
        try {
            const res = await axiosInstance.post(`/api/finance/fixed-assets/assets/${id}/activate`, {});
            if (res.data?.success) {
                enqueueSnackbar('Asset activated', { variant: 'success' });
                fetchAssets();
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Activation failed', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PrecisionManufacturingIcon sx={{ fontSize: 30, color: '#fff' }} />
                        <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.6rem' }}>
                            Asset Register
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.25 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchAssets} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/finance-portal/fixed-assets/assets/new')}
                            sx={{
                                fontFamily: FA_FONT, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: FA_ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' },
                            }}
                        >
                            New Asset
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1500, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: FA_FONT }}>{error}</Alert>}

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" placeholder="Search by name, number, tag, serial..."
                        value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px', minWidth: 320 } }}
                    />
                    <TextField
                        size="small" select label="Status" value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }}
                        sx={{ minWidth: 180 }}
                    >
                        {STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily: FA_FONT }}>{s.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" select label="Category" value={categoryFilter}
                        onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="" sx={{ fontFamily: FA_FONT }}>All Categories</MenuItem>
                        {categories.map(c => (
                            <MenuItem key={c.id} value={c.id} sx={{ fontFamily: FA_FONT }}>{c.name}</MenuItem>
                        ))}
                    </TextField>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/finance-portal/fixed-assets/categories')}
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', borderRadius: '10px', borderColor: '#e2e8f0', color: '#475569' }}
                    >
                        Manage Categories
                    </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Asset #', 'Name', 'Category', 'Cost', 'Acc. Dep.', 'Book Value', 'Status', 'In Service', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8, 9].map(j => (
                                            <TableCell key={j}><Skeleton height={32} /></TableCell>
                                        ))}</TableRow>
                                    ))
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                                            <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>
                                                No assets found. Create your first one to get started.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map(a => (
                                    <TableRow key={a.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600, color: FA_ACCENT_DARK }}>{a.asset_number}</TableCell>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{a.name}</TableCell>
                                        <TableCell sx={cellSx}>{a.category_name || '\u2014'}</TableCell>
                                        <TableCell sx={cellSx}>{formatCurrency(a.total_capitalized_cost)}</TableCell>
                                        <TableCell sx={cellSx}>{formatCurrency(a.book_accumulated_depreciation)}</TableCell>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{formatCurrency(a.book_value)}</TableCell>
                                        <TableCell sx={cellSx}>
                                            <Chip
                                                label={humanizeStatus(a.status)}
                                                size="small"
                                                sx={{ fontFamily: FA_FONT, fontWeight: 600, fontSize: '0.7rem',
                                                    ...statusChip(a.status, ASSET_STATUS_COLORS) }}
                                            />
                                        </TableCell>
                                        <TableCell sx={cellSx}>{formatDate(a.in_service_date)}</TableCell>
                                        <TableCell sx={cellSx}>
                                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                                                <Tooltip title="View">
                                                    <IconButton size="small" onClick={() => navigate(`/finance-portal/fixed-assets/assets/${a.id}`)}>
                                                        <VisibilityIcon sx={{ fontSize: 18, color: '#475569' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => navigate(`/finance-portal/fixed-assets/assets/${a.id}/edit`)}>
                                                        <EditIcon sx={{ fontSize: 18, color: '#475569' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {(a.status === 'DRAFT' || a.status === 'IN_PROGRESS') && (
                                                    <Tooltip title="Activate (place in service)">
                                                        <IconButton size="small" onClick={() => handleActivate(a.id)}>
                                                            <PlayCircleOutlineIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={perPage}
                        onRowsPerPageChange={e => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{ '& *': { fontFamily: FA_FONT } }}
                    />
                </Card>
            </Box>
        </Box>
    );
}
